/**
 * Machine resources and long jobs (docs/machine-resources.md).
 *
 * A machine-wide broker in vikod grants named resources (`screen`, `heavy`, `ship:<lane>`,
 * `unity-editor:<project>`, …) to runs, landing gates and release lanes. Agents reach it through
 * `viko lock <res> -- <cmd>` (CLI) or the `viko_lock` MCP tools, and wait on long jobs with
 * `viko_wait_for` instead of `sleep` polling. Additive only.
 */
import { z } from 'zod';
/**
 * A resource name: a lowercase kind with an optional `:<qualifier>` (`heavy`, `screen`,
 * `ship:webb`, `unity-editor:escargo`). The kind decides the default capacity.
 */
export const ResourceName = z
    .string()
    .regex(/^[a-z][a-z0-9-]{0,39}(:[A-Za-z0-9._-]{1,80})?$/, 'resource names look like heavy, screen or ship:web');
/** The resource only granted while the user is away (or after a one-tap approval). */
export const SCREEN_RESOURCE = 'screen';
export const HEAVY_RESOURCE = 'heavy';
export const LeasePriority = z.enum(['low', 'normal', 'high']);
export const LeaseState = z.enum([
    'waiting',
    'granted',
    'refused',
    'revoked',
    'released',
    'expired',
]);
export const LeaseHolderKind = z.enum(['run', 'landing', 'lane', 'cli']);
/** Why a screen ticket is waiting (or was granted). */
export const ScreenGate = z.enum(['away', 'approved', 'awaiting-user', 'declined']);
// --- MCP: viko_lock / viko_lock_renew / viko_unlock / viko_resources ---------------------------
export const VikoLockInput = z.object({
    /** Resources to hold together (all or nothing), e.g. ["screen"] or ["heavy", "ship:web"]. */
    resources: z.array(ResourceName).min(1).max(8),
    /** What will run under the lease (shown in the queue; scrubbed). */
    command: z.string().max(2000).optional(),
    /** Rough duration, shown on the screen approval ("Run UI tests now? ~N min"). */
    estimateMin: z
        .number()
        .int()
        .positive()
        .max(24 * 60)
        .optional(),
    /** How long this call may block waiting (default 120 s, max 300 s). 0 = just enqueue. */
    waitSec: z.number().int().min(0).max(300).optional(),
    /** Resume waiting on a ticket from an earlier call (keeps the queue position). */
    ticketId: z.string().min(1).max(80).optional(),
    /**
     * Lease time-to-live without a renew (default 15 min, max 4 h). `viko lock` renews
     * automatically; an MCP-held lease ends at the TTL, on `viko_unlock` or when the run ends.
     */
    holdSec: z
        .number()
        .int()
        .min(10)
        .max(4 * 3600)
        .optional(),
    priority: LeasePriority.optional(),
});
export const LeaseHolderView = z.object({
    ticketId: z.string(),
    kind: LeaseHolderKind,
    /** "this run", the other run's project/workstream label, or "another project". */
    label: z.string(),
    resources: z.array(ResourceName),
    since: z.number(),
});
export const VikoLockOutput = z.object({
    ticketId: z.string(),
    status: LeaseState,
    /** 1 = next in line. */
    position: z.number().int().positive().optional(),
    /** Who holds what this ticket needs (waiting tickets). */
    blockedBy: z.array(LeaseHolderView).max(16).optional(),
    /** Plain-language reason (refused / revoked / waiting on the user). */
    reason: z.string().optional(),
    screenGate: ScreenGate.optional(),
    /** Granted: the lease ends at this time unless renewed. */
    expiresAt: z.number().optional(),
    waitedMs: z.number().int().nonnegative(),
});
export const VikoLockRenewInput = z.object({
    ticketId: z.string().min(1).max(80),
    holdSec: z
        .number()
        .int()
        .min(10)
        .max(4 * 3600)
        .optional(),
});
export const VikoLockRenewOutput = z.object({
    status: LeaseState,
    expiresAt: z.number().optional(),
    /** Revoked: stop the command now (e.g. "the user is back at the screen"). */
    reason: z.string().optional(),
});
export const VikoUnlockInput = z.object({ ticketId: z.string().min(1).max(80) });
export const VikoUnlockOutput = z.object({ released: z.boolean() });
export const VikoResourcesInput = z.object({});
export const ResourceView = z.object({
    name: ResourceName,
    capacity: z.number().int().nonnegative(),
    inUse: z.number().int().nonnegative(),
    waiting: z.number().int().nonnegative(),
});
export const VikoResourcesOutput = z.object({
    resources: z.array(ResourceView),
    holders: z.array(LeaseHolderView).max(64),
    waiting: z.array(LeaseHolderView).max(64),
    userAway: z.boolean().optional(),
    disk: z.object({ freeGb: z.number(), floorGb: z.number(), belowFloor: z.boolean() }).optional(),
});
// --- MCP: viko_wait_for ------------------------------------------------------------------------
export const WaitCondition = z.discriminatedUnion('kind', [
    /** A process this run started (it must descend from the run's agent process). */
    z.object({ kind: z.literal('process'), pid: z.number().int().positive() }),
    /** A file exists, or (with `changedAfter`, epoch ms) its mtime passes that time. */
    z.object({
        kind: z.literal('file'),
        path: z.string().min(1).max(4096),
        changedAfter: z.number().optional(),
    }),
    /** A line matching `pattern` (JavaScript regex) appears in a log file (new lines by default). */
    z.object({
        kind: z.literal('log'),
        path: z.string().min(1).max(4096),
        pattern: z.string().min(1).max(500),
        flags: z
            .string()
            .regex(/^[imsu]*$/)
            .optional(),
        /** Also match lines already in the file (default false: only lines written after the call). */
        fromStart: z.boolean().optional(),
    }),
    /** An http(s) URL answers with `status` (default 2xx) and, optionally, a body matching `bodyPattern`. */
    z.object({
        kind: z.literal('url'),
        url: z.string().url().max(2000),
        status: z.number().int().min(100).max(599).optional(),
        bodyPattern: z.string().max(500).optional(),
    }),
]);
export const WaitState = z.enum(['pending', 'met', 'timeout', 'failed', 'cancelled']);
export const VikoWaitForInput = z.object({
    /** New wait: the condition. Omit when resuming with `waitId`. */
    condition: WaitCondition.optional(),
    /** Resume (or poll) an earlier wait. */
    waitId: z.string().min(1).max(80).optional(),
    /** Give up after this long (default 1 h, max 24 h). */
    timeoutSec: z
        .number()
        .int()
        .min(1)
        .max(24 * 3600)
        .optional(),
    /** How long this call blocks before returning `pending` (default 50 s, max 300 s). */
    waitSec: z.number().int().min(0).max(300).optional(),
    /** Shown in the run's activity and the wake-up message ("iOS build", "staging deploy"). */
    label: z.string().max(200).optional(),
    /** When the wait finishes while this run is idle or parked, wake it with a message (default true). */
    wake: z.boolean().optional(),
    /** Stop a pending wait. */
    cancel: z.boolean().optional(),
});
export const VikoWaitForOutput = z.object({
    waitId: z.string(),
    status: WaitState,
    /** What happened, in one line ("exited 0", "matched: BUILD SUCCEEDED", "HTTP 200"). */
    detail: z.string().optional(),
    elapsedMs: z.number().int().nonnegative(),
    exitCode: z.number().int().optional(),
    match: z.string().max(2000).optional(),
    httpStatus: z.number().int().optional(),
});
export const MACHINE_RESOURCE_MCP_TOOLS = {
    viko_lock: { input: VikoLockInput, output: VikoLockOutput },
    viko_lock_renew: { input: VikoLockRenewInput, output: VikoLockRenewOutput },
    viko_unlock: { input: VikoUnlockInput, output: VikoUnlockOutput },
    viko_resources: { input: VikoResourcesInput, output: VikoResourcesOutput },
    viko_wait_for: { input: VikoWaitForInput, output: VikoWaitForOutput },
};
/** Tools a run's shell (`viko lock`, `viko wait`) may call with its resource-only token. */
export const RESOURCE_TOKEN_TOOLS = Object.keys(MACHINE_RESOURCE_MCP_TOOLS);
// --- machine.status: the broker's queue for You › Machines (sealed in the machine report) ------
export const MachineResourceLease = z.object({
    ticketId: z.string().max(80),
    kind: LeaseHolderKind,
    state: LeaseState,
    resources: z.array(ResourceName).max(8),
    /** Project id/name and workstream or lane label (sealed report: never plaintext on the hub). */
    projectId: z.string().max(200).optional(),
    label: z.string().max(300),
    runId: z.string().max(200).optional(),
    command: z.string().max(500).optional(),
    priority: LeasePriority,
    enqueuedAt: z.number(),
    grantedAt: z.number().optional(),
    expiresAt: z.number().optional(),
    estimateMin: z.number().optional(),
    screenGate: ScreenGate.optional(),
    reason: z.string().max(500).optional(),
});
export const DiskCleanupSuggestion = z.object({
    /** What it is ("Xcode DerivedData", "merged Viko worktrees"). */
    title: z.string().max(200),
    path: z.string().max(1000).optional(),
    sizeGb: z.number().optional(),
    /** A safe command the user can run (never removes uncommitted work). */
    command: z.string().max(1000).optional(),
    note: z.string().max(500).optional(),
});
export const MachineResourcesStatus = z.object({
    ts: z.number(),
    resources: z.array(ResourceView).max(64),
    leases: z.array(MachineResourceLease).max(128),
    userAway: z.boolean().optional(),
    disk: z
        .object({
        path: z.string().max(1000),
        freeGb: z.number(),
        floorGb: z.number(),
        belowFloor: z.boolean(),
        suggestions: z.array(DiskCleanupSuggestion).max(16).optional(),
    })
        .optional(),
    /** Recently finished leases (newest first) for the drill-down. */
    recent: z.array(MachineResourceLease).max(32).optional(),
});
//# sourceMappingURL=machine-resources.js.map